/**
 * This file is based on the react-native-square-grid library from
 * https://github.com/RangerMauve/react-native-square-grid
 *
 * MIT License
 *
 * Copyright (c) 2017 RangerMauve
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

/*
Component to render multiple items as a grid of squares.
The maximum size of squares still fitting on the screen is calculated.
The remaining space to the top, bottom and sides is filled out with a padding.
Verical Scrolling can be enabled by setting rows={0}

example usage for a 2x2 grid of squares:
 <SquareGrid
      rows={2}
      columns={2}
      items={[
        <Text>Text</Text>,
        <View>View</View>,
        <Image />,
        <Text>Text</Text>
      ]}
    />
*/

import { ReactNode, useState } from 'react';
import { LayoutChangeEvent, ScrollView, View, ViewStyle } from 'react-native';
import { styles } from './styles';

interface SquareGridProps {
  items: ReactNode[];
  rows?: number;
  columns?: number;
  stretch?: boolean;
  itemMargin?: number;
}

// creates the rendered item and centers it
const renderItem = (item: ReactNode) => (
  <View style={styles.item}>
    <View style={styles.content}>
      <View style={styles.stretch}>{item}</View>
    </View>
  </View>
);

export const SquareGrid = ({
  items,
  rows = 0,
  columns = 0,
  stretch = false,
  itemMargin = 0,
}: SquareGridProps) => {
  const [layout, setLayout] = useState({ width: 0, height: 0 });

  const handleLayout = (event: LayoutChangeEvent) => {
    const { width, height } = event.nativeEvent.layout;
    setLayout({ width, height });
  };

  const width = layout.width - 2;
  const height = layout.height - 2;

  if (!rows && !columns) {
    // eslint-disable-next-line no-console
    console.error('Must specify number of rows or columns');
    return <View />;
  } else if (!columns) {
    // eslint-disable-next-line no-console
    console.error('Must specify number of columns');
    return <View />;
  }

  let marginHorizontal = 0;
  let marginVertical = 0;
  let size;

  // if 0 rows are passed, full width vertical scrolling is used
  const isScrolling = !rows;
  if (isScrolling) {
    size = Math.floor(width / columns);
  } else {
    size = Math.min(width / columns, height / rows);
    marginHorizontal = Math.floor((width - size * columns) / 2);
    marginVertical = Math.floor((height - size * rows) / 2);
    size = Math.floor(size);
  }

  const itemStyle: ViewStyle = {
    width: size,
    height: size,
  };

  // for the non-scrolling SquareGrid a padding
  const containerStyle = {
    paddingHorizontal: stretch ? 0 : marginHorizontal,
    paddingVertical: marginVertical,
  };

  // if stretch prop is set true, add a margin to the left and right so the items are aligned stretched
  if (stretch) {
    itemStyle.marginHorizontal = marginHorizontal / columns;
  }

  if (itemMargin !== 0) {
    itemStyle.padding = itemMargin;
  }

  // display a maximum of rows*columns items or infinite for scrolling
  const maxItems = isScrolling ? Infinity : rows * columns;

  // items to render
  const renderedItems = items.slice(0, maxItems).map((item, index) => (
    <View key={index}>
      <View style={itemStyle}>{renderItem(item)}</View>
    </View>
  ));

  // scrolling SquareGrid
  if (isScrolling) {
    return (
      <ScrollView style={styles.scrollContainer}>
        <View style={styles.gridContainer} onLayout={handleLayout}>
          {renderedItems}
        </View>
      </ScrollView>
    );
  }

  // non scrolling SquareGrid
  return (
    <View
      style={[styles.gridContainer, containerStyle]}
      onLayout={handleLayout}
    >
      {renderedItems}
    </View>
  );
};
